import { useQueryClient } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import Sidebar from "~/components/Sidebar/Sidebar";
import { PROJECT_DETAILS_PATH_WITH_TAB_ID } from "~/constants/appRoutes.ts";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import { All_LOCATIONS_QUERY_KEY } from "~/modules/location/api/location/locationQueries.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import { PROJECT_QUERY_KEY } from "~/modules/project/api/project/projectQueries.ts";
import { PROJECTS_PHASES_QUERY_KEY } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";
import CreateProjectPhaseForm from "~/modules/project/components/forms/CreateProjectPhaseForm";
import { generatePhaseTabId } from "~/modules/project/utils/projectDetailsViewUtils.ts";

type CreateProjectPhaseSidebarPhase = {
	isOpen: boolean;
	clientId: string;
	projectId: string;
	setOpen: (open: boolean) => void;
	setSelectedPhaseId: (value: string) => void;
	projectBillingSettings?: BillingSettings;
	projectBillingSettingsIsLoading: boolean;
};

const CreateProjectPhaseSidebar: FunctionComponent<CreateProjectPhaseSidebarPhase> = ({
	isOpen,
	clientId,
	projectBillingSettings,
	projectBillingSettingsIsLoading,
	projectId,
	setOpen,
}) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const handleSuccess = useCallback(
		async (newPhaseNumber: number) => {
			await queryClient.refetchQueries({ queryKey: PROJECTS_PHASES_QUERY_KEY(projectId) });
			await queryClient.invalidateQueries({ queryKey: orderQueries.byProject(projectId).queryKey });
			await queryClient.invalidateQueries({ queryKey: PROJECT_QUERY_KEY(projectId) });
			await queryClient.invalidateQueries({ queryKey: All_LOCATIONS_QUERY_KEY });
			navigate(generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
				projectId,
				tabId: generatePhaseTabId(newPhaseNumber),
			}));
			setOpen(false);
		},
		[navigate, projectId, queryClient, setOpen],
	);

	return (
		<Sidebar
			closeOnOutsideClick={false}
			open={isOpen}
			setOpen={() => setOpen(!isOpen)}>
			{isOpen && projectBillingSettingsIsLoading && <SidebarBusyOverlay />}
			{isOpen && projectBillingSettings && <CreateProjectPhaseForm
				clientId={clientId}
				projectBillingSettings={projectBillingSettings}
				projectId={projectId}
				onSuccess={handleSuccess}
				onCancel={() => setOpen(false)} />}
		</Sidebar>
	);
};

export default CreateProjectPhaseSidebar;
